import { createFileRoute } from "@tanstack/react-router";
import { faceCopy } from "geom/brep";
import { mat4Mult, mat4Rotate, mat4Translate } from "geom/mat4";
import type { DCEL } from "geom/polyclip/dcel";
import type { Segment } from "geom/polyclip/dcel";
import { renderDCEL, renderDCELEdges, renderDCELVerts } from "geom/polyclip/render";
import { TrimFaceWithFacesTestCases } from "geom/polyclip/test/trimFaceWithFacesTestCases";
import type { HalfEdgeClassification } from "geom/polyclip/trimFaceWithFaces";
import { buildFaceTrimmingClassifications, trimFaceWithFaces } from "geom/polyclip/trimFaceWithFaces";
import { transformFace } from "geom/transform";
import { defaultCameraSettings } from "gfx/CameraSettings";
import { debugPlaneToTHREE, faceToTHREE, polyLine2ToTHREE } from "gfx/convert";
import { EnvPreset } from "gfx/EnvPreset";
import { envPresetSceneLights } from "gfx/sceneLights";
import THREEViewer from "gfx/THREEViewer";
import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import { DoubleSide, MeshBasicMaterial } from "three";
import colors from "ui/styles/colors";
import Canvas from "./-Canvas";

function TrimFacesTestPage() {
  const [viewer, setViewer] = useState<THREEViewer | null>(null);
  const viewerContainer = useRef<HTMLDivElement>(null);
  const [testIndex, setTestIndex] = useState(0);
  const [showSubject, setShowSubject] = useState(true);
  const [fillResult, setFillResult] = useState(true);
  const [showCutFaces, setShowCutFaces] = useState(true);
  const [dcel, setDCEL] = useState<DCEL | null>(null);
  const [halfEdgeClassifications, setHalfEdgeClassifications] = useState<HalfEdgeClassification[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [translateZ, setTranslateZ] = useState(0);
  const [rotateZ, setRotateZ] = useState(0);

  useEffect(() => {
    const viewerInstance = new THREEViewer({
      container: viewerContainer.current as HTMLDivElement,
      options: {
        gui: false,
        shadows: true,
        animate: true,
        showAxes: false,
        ssfxRenderer: true,
        debugFeaturesEnabled: false,
        sceneLights: envPresetSceneLights[EnvPreset.WHITEBOX_LOW_FOG],
        cameraSettings: {
          ...defaultCameraSettings,
          targetX: 0,
          targetY: 0,
          targetZ: 0,
          x: 500,
          y: -500,
          z: 500,
        },
      },
    });
    viewerInstance.controls.maxPolarAngle = Math.PI;
    viewerInstance.controls.update();

    setViewer(viewerInstance);

    return () => {
      viewerInstance.dispose();
      setViewer(null);
    };
  }, []);

  useEffect(() => {
    if (!viewer) {
      return undefined;
    }

    const c = TrimFaceWithFacesTestCases[testIndex];

    // Create transformation matrix
    const rotate = mat4Rotate(rotateZ / (2 * Math.PI), 0, 0, 1);
    const translate = mat4Translate(translateX, translateY, translateZ);
    const transform = rotate ? mat4Mult(translate, rotate) : translate;

    // Transform the face
    const transformedFace = transformFace(faceCopy(c.face), transform);

    if (showSubject) {
      for (const loop of transformedFace.loops) {
        viewer.add(polyLine2ToTHREE({ path: loop, plane: transformedFace.plane }));
      }
    }

    if (showCutFaces) {
      for (const cutFace of c.cutFaces) {
        viewer.add(
          faceToTHREE(
            cutFace,
            new MeshBasicMaterial({
              color: colors.blue,
              transparent: true,
              opacity: 0.2,
              side: DoubleSide,
            }),
          ),
        );
        // Add debug plane visualization
        viewer.add(debugPlaneToTHREE(cutFace.plane));
      }
    }

    const {
      dcel: newDCEL,
      segments: newSegments,
      halfEdgeClassifications,
    } = buildFaceTrimmingClassifications(transformedFace, c.cutFaces);
    setDCEL(newDCEL);
    setSegments(newSegments);
    setHalfEdgeClassifications(halfEdgeClassifications);

    const res = trimFaceWithFaces(transformedFace, c.cutFaces);

    for (const face of res.faces ?? []) {
      if (fillResult) {
        viewer.add(
          faceToTHREE(
            face,
            new MeshBasicMaterial({
              color: colors.green,
              side: DoubleSide,
            }),
          ),
        );
      }

      for (const loop of face.loops) {
        const r = polyLine2ToTHREE({ path: loop, plane: face.plane });
        viewer.add(r);
      }
    }

    return () => {
      viewer.scene.clear();
    };
  }, [viewer, testIndex, showSubject, showCutFaces, fillResult, translateX, translateY, translateZ, rotateZ]);

  const renderTest = useCallback(
    (canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, scale: number) => {
      if (!dcel) {
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save();

      // Get the bounding box of the DCEL vertices
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;

      for (const vertex of dcel.vertices) {
        minX = Math.min(minX, vertex[0]);
        minY = Math.min(minY, vertex[1]);
        maxX = Math.max(maxX, vertex[0]);
        maxY = Math.max(maxY, vertex[1]);
      }

      const PADDING = 10;
      minX -= PADDING;
      minY -= PADDING;
      maxX += PADDING;
      maxY += PADDING;

      const width = maxX - minX;
      const height = maxY - minY;

      const scaleFactor = Math.min(canvas.width / width, canvas.height / height);
      const ratio = window.devicePixelRatio;
      const ss = scaleFactor / ratio;

      ctx.translate(-ss * minX, canvas.height / ratio + ss * minY);
      ctx.scale(ss, -ss);

      renderDCEL(ctx, dcel);
      renderDCELEdges(ctx, dcel, scale, undefined, undefined, (halfEdgeIndex) => {
        return halfEdgeClassifications[halfEdgeIndex][0];
      });
      renderDCELVerts(ctx, dcel, scale);

      // Render segments
      ctx.strokeStyle = "red";
      ctx.lineWidth = 0.5;
      for (const segment of segments) {
        ctx.beginPath();
        ctx.moveTo(segment[0], segment[1]);
        ctx.lineTo(segment[2], segment[3]);
        ctx.stroke();
      }

      ctx.restore();
    },
    [dcel, segments],
  );

  return (
    <>
      <Head>
        <style>{`
          fieldset div {
            height: 2em;
            display: flex;
            flex-direction: row;
            align-items: center;
          }

          fieldset div label {
            width: 8em;
            text-align: right;
            padding-right: 1em;
          }
        `}</style>
      </Head>
      <div
        style={{
          padding: 5,
          border: "1px solid black",
          position: "absolute",
          top: 0,
          right: 0,
          width: 500,
          background: "white",
          zIndex: 100,
        }}
      >
        <Canvas canvasWidth={500} canvasHeight={500} render={renderTest} />
      </div>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 100,
        }}
        ref={viewerContainer}
      >
        <fieldset style={{ display: "flex", flexDirection: "column" }}>
          <div>
            <label htmlFor="testCase">Test case</label>
            <select
              name="testCase"
              value={testIndex}
              onChange={(x) => {
                setTestIndex(parseInt(x.target.value));
                setTranslateX(0);
                setTranslateY(0);
                setTranslateZ(0);
                setRotateZ(0);
              }}
            >
              {TrimFaceWithFacesTestCases.map((c, i) => (
                <option key={i} value={i}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="showSubject">Show subject</label>
            <input
              name="showSubject"
              type="checkbox"
              checked={showSubject}
              onChange={() => setShowSubject(!showSubject)}
            />
          </div>

          <div>
            <label htmlFor="showCutFaces">Show cut faces</label>
            <input
              name="showCutFaces"
              type="checkbox"
              checked={showCutFaces}
              onChange={() => setShowCutFaces(!showCutFaces)}
            />
          </div>

          <div>
            <label htmlFor="fillResult">Fill result</label>
            <input name="fillResult" type="checkbox" checked={fillResult} onChange={() => setFillResult(!fillResult)} />
          </div>

          <div>
            <label htmlFor="translateX">Translate X</label>
            <input
              name="translateX"
              type="range"
              step={0.001}
              min={-200}
              max={200}
              value={translateX}
              onChange={(x) => setTranslateX(parseFloat(x.target.value))}
            />
            <input
              type="number"
              value={translateX}
              min={-200}
              max={200}
              step={0.001}
              onChange={(e) => setTranslateX(parseFloat(e.target.value))}
              style={{
                width: "80px",
                padding: "5px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
          </div>

          <div>
            <label htmlFor="translateY">Translate Y</label>
            <input
              name="translateY"
              type="range"
              step={0.001}
              min={-200}
              max={200}
              value={translateY}
              onChange={(x) => setTranslateY(parseFloat(x.target.value))}
            />
            <input
              type="number"
              value={translateY}
              min={-200}
              max={200}
              step={0.001}
              onChange={(e) => setTranslateY(parseFloat(e.target.value))}
              style={{
                width: "80px",
                padding: "5px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
          </div>

          <div>
            <label htmlFor="translateZ">Translate Z</label>
            <input
              name="translateZ"
              type="range"
              step={0.001}
              min={-200}
              max={200}
              value={translateZ}
              onChange={(x) => setTranslateZ(parseFloat(x.target.value))}
            />
            <input
              type="number"
              value={translateZ}
              min={-200}
              max={200}
              step={0.001}
              onChange={(e) => setTranslateZ(parseFloat(e.target.value))}
              style={{
                width: "80px",
                padding: "5px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
          </div>

          <div>
            <label htmlFor="rotateZ">Rotate Z</label>
            <input
              name="rotateZ"
              type="range"
              step={0.001}
              min={-180}
              max={180}
              value={rotateZ}
              onChange={(x) => setRotateZ(parseFloat(x.target.value))}
            />
            <input
              type="number"
              value={rotateZ}
              min={-180}
              max={180}
              step={0.001}
              onChange={(e) => setRotateZ(parseFloat(e.target.value))}
              style={{
                width: "80px",
                padding: "5px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
          </div>
        </fieldset>
      </div>
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
        ref={viewerContainer}
      />
    </>
  );
}

export const Route = createFileRoute("/debug/polyclip/trim-faces-test")({
  component: TrimFacesTestPage,
});
